import React, { useEffect, useMemo, useState } from 'react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { GetAction } from '../../Component/GetAction'
import PaymentReceipt from '../../View/PaymentReceipt'
import ManageTable from '../ManageTable'
import {
  RequireModule,
  PageHeader,
  formatInr,
  downloadCsv,
} from './shared'

function DonationReportsPage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [receipt, setReceipt] = useState(null)
  const [receiptAutoDownload, setReceiptAutoDownload] = useState(false)

  const openReceipt = (row, autoDownload = false) => {
    setReceiptAutoDownload(!!autoDownload)
    setReceipt(row)
  }

  const closeReceipt = () => {
    setReceipt(null)
    setReceiptAutoDownload(false)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = await GetAction('get', { status: 'paid' }, 'donation/donationList')
      if (cancelled) return
      if (data?.status === 'success' && Array.isArray(data.response)) {
        setRows(data.response)
        setError('')
      } else {
        setError(data?.message || 'Failed to load donation report.')
        setRows([])
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const tableRows = useMemo(
    () => rows.map((row, i) => ({ ...row, index: i + 1 })),
    [rows]
  )

  const totalAmount = useMemo(
    () => tableRows.reduce((sum, row) => sum + (Number(row.payment_amount) || 0), 0),
    [tableRows]
  )

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    {
      key: 'name',
      label: 'Name',
      render: (row) => {
        const label = row.name || '—'
        const guest = Number(row.is_guest) === 1 || row.donor_kind === 'Guest'
        return guest && !String(label).includes('(Guest)') ? `${label} (Guest)` : label
      },
    },
    {
      key: 'donor_kind',
      label: 'Donor',
      render: (row) =>
        Number(row.is_guest) === 1 || row.donor_kind === 'Guest' ? 'Guest' : 'Registered',
    },
    {
      key: 'mobile',
      label: 'Mobile',
      render: (row) => row.mobile || '—',
    },
    {
      key: 'type_name',
      label: 'Donation Type',
      render: (row) => row.type_name || row.donation_type_name || '—',
    },
    {
      key: 'payment_amount',
      label: 'Amount',
      render: (row) => formatInr(row.payment_amount),
    },
    { key: 'created_at', label: 'Date' },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openReceipt(row, false)}
            title="View receipt"
          >
            View
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => openReceipt(row, true)}
            title="Download PDF receipt"
          >
            <i className="fa fa-file-pdf-o" aria-hidden="true" /> PDF
          </button>
        </div>
      ),
    },
  ]

  const exportRows = () => {
    downloadCsv(
      'donation-reports.csv',
      [
        { key: 'index', label: '#' },
        { key: 'name', label: 'Name' },
        { key: 'mobile', label: 'Mobile' },
        {
          key: 'type_name',
          label: 'Donation Type',
          get: (r) => r.type_name || r.donation_type_name || '',
        },
        { key: 'payment_amount', label: 'Amount' },
        { key: 'created_at', label: 'Date' },
        { key: 'transaction_id', label: 'Txn' },
      ],
      tableRows
    )
  }

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    doc.setFontSize(16)
    doc.text('Donation Report', 14, 16)
    doc.setFontSize(10)
    doc.text(`Records: ${tableRows.length}`, 14, 24)

    autoTable(doc, {
      startY: 30,
      head: [['#', 'Name', 'Mobile', 'Donation Type', 'Amount', 'Date']],
      body: tableRows.map((row) => [
        row.index,
        row.name || '',
        row.mobile || '',
        row.type_name || row.donation_type_name || '',
        formatInr(row.payment_amount),
        row.created_at || '',
      ]),
      foot: [['', '', '', 'Total amount', formatInr(totalAmount), '']],
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [253, 126, 20], textColor: 255, fontStyle: 'bold' },
      footStyles: {
        fillColor: [255, 248, 240],
        textColor: [196, 90, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 12 },
        4: { halign: 'right' },
      },
    })

    doc.save('donation-report.pdf')
  }

  return (
    <RequireModule moduleKey="donation_reports">
      <PageHeader
        title="Donation Reports"
        subtitle="Paid donations — view or download PDF receipts."
      />
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={tableRows}
            searchKeys={[
              'name',
              'mobile',
              'type_name',
              'donation_type_name',
              'payment_amount',
              'created_at',
            ]}
            emptyMessage="No paid donations found."
            footer={(visible) => {
              const visibleTotal = visible.reduce(
                (sum, row) => sum + (Number(row.payment_amount) || 0),
                0
              )
              return visible.length ? (
                <tr>
                  <td colSpan={4} className="text-right text-end">
                    Total amount
                  </td>
                  <td>{formatInr(visibleTotal)}</td>
                  <td colSpan={2} />
                </tr>
              ) : null
            }}
          />
        )}
        {!loading ? (
          <div className="manage-export-bar">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              onClick={exportRows}
              disabled={!tableRows.length}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={exportPdf}
              disabled={!tableRows.length}
              title="Download table as PDF"
            >
              <i className="fa fa-file-pdf-o" aria-hidden="true" /> Export PDF
            </button>
          </div>
        ) : null}
      </div>
      {receipt ? (
        <PaymentReceipt
          hide={closeReceipt}
          title="Donation History"
          data={receipt}
          autoDownload={receiptAutoDownload}
        />
      ) : null}
    </RequireModule>
  )
}

export default DonationReportsPage
